/**
 * TimeTrack - API Client
 *
 * Echter HTTP-Client für Server-Kommunikation.
 * Nutzt Zod-validierte Types aus @timetrack/contract.
 */

import type {
  SyncRequest,
  TimeSheet as ContractTimeSheet,
  TimeEntry as ContractTimeEntry,
} from '@timetrack/contract';
import type { TimeSheet, TimeEntry } from '@timetrack/domain';
import { getDeviceId } from '../data/db';

// =============================================================================
// Types
// =============================================================================

export interface SyncResult {
  success: boolean;
  /** Zeitpunkt der Synchronisation (ISO-8601) */
  syncedAt?: string;
  /** IDs der synchronisierten Sheets */
  syncedSheets?: string[];
  /** Fehlermeldung (falls nicht erfolgreich) */
  error?: string;
}

export type ApiError = {
  type: 'network' | 'server' | 'validation';
  message: string;
  statusCode?: number;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Konvertiert Domain-Types (camelCase) zu Contract-Types (snake_case).
 * Contract nutzt snake_case für Konsistenz mit DB-Schema.
 */
function toContractTimeEntry(entry: TimeEntry): ContractTimeEntry {
  return {
    id: entry.id,
    time_sheet_id: entry.timeSheetId,
    employee_id: entry.employeeId,
    personnel_number: entry.personnelNumber,
    employee_name: entry.employeeName,
    type: entry.type,
    status: entry.status,
    start_time: entry.startTime,
    end_time: entry.endTime,
    break_minutes: entry.breakMinutes,
    start_location: entry.startLocation,
    end_location: entry.endLocation,
    note: entry.note,
    created_at: entry.createdAt,
    updated_at: entry.updatedAt,
    version: entry.version,
  };
}

function toContractTimeSheet(sheet: TimeSheet, entries: TimeEntry[]): ContractTimeSheet {
  return {
    id: sheet.id,
    date: sheet.date,
    foreman_id: sheet.foremanId,
    supervisor_id: sheet.supervisorId,
    cost_center_id: sheet.costCenterId,
    status: sheet.status,
    ta_locked: sheet.taLocked,
    approved_by: sheet.approvedBy,
    approved_at: sheet.approvedAt,
    note: sheet.note,
    submission_location: sheet.submissionLocation,
    created_at: sheet.createdAt,
    updated_at: sheet.updatedAt,
    version: sheet.version,
    entries: entries.map(toContractTimeEntry),
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Synchronisiert ein TimeSheet mit dem Server.
 *
 * @param sheet - Das zu synchronisierende TimeSheet
 * @param entries - Die zugehoerigen TimeEntries
 * @returns SyncResult mit Erfolgs-Status
 *
 * @example
 * const result = await syncTimeSheet(sheet, entries);
 * if (result.success) {
 *   console.info('Synced at:', result.syncedAt);
 * } else {
 *   console.error('Sync failed:', result.error);
 * }
 */
export async function syncTimeSheet(
  sheet: TimeSheet,
  entries: TimeEntry[]
): Promise<SyncResult> {
  console.info('[API] Syncing TimeSheet:', sheet.id);

  try {
    const deviceId = await getDeviceId();

    const syncRequest: SyncRequest = {
      client_id: deviceId,
      sheets: [toContractTimeSheet(sheet, entries)],
    };

    const response = await fetch('/api/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(syncRequest),
    });

    if (!response.ok) {
      // Parse error response
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // Ignore JSON parse error, use default message
      }

      console.error('[API] Sync failed:', errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }

    const data = await response.json();
    console.info('[API] Sync successful:', data);

    return {
      success: true,
      syncedAt: data.syncedAt,
      syncedSheets: data.syncedSheets,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('[API] Network error:', message);

    return {
      success: false,
      error: `Netzwerkfehler: ${message}`,
    };
  }
}

/**
 * Health Check - prüft ob der Server erreichbar ist.
 * Nützlich für Online/Offline-Detection.
 */
export async function checkServerHealth(): Promise<boolean> {
  try {
    const response = await fetch('/api/health', {
      method: 'GET',
      // Timeout nach 5 Sekunden
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      console.warn('[API] Health check failed: HTTP', response.status);
      return false;
    }

    const data = await response.json();
    console.info('[API] Health check passed:', data);
    return true;
  } catch (error) {
    console.warn('[API] Health check failed:', error);
    return false;
  }
}

// =============================================================================
// Stammdaten-Sync (Employees)
// =============================================================================

export interface ServerEmployee {
  id: string;
  personnelNumber: string;
  firstName: string;
  lastName: string;
  foremanId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Holt alle Mitarbeiter vom Server.
 * Für Stammdaten-Sync beim App-Start.
 *
 * @returns ServerEmployee[] bei Erfolg (kann leer sein), null bei Fehler
 */
export async function fetchEmployees(): Promise<ServerEmployee[] | null> {
  console.info('[API] Fetching employees from server');

  try {
    const response = await fetch('/api/admin/employees', {
      method: 'GET',
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      console.warn('[API] Failed to fetch employees: HTTP', response.status);
      return null;
    }

    const data = await response.json();
    console.info('[API] Fetched', data.employees?.length ?? 0, 'employees');
    return data.employees ?? [];
  } catch (error) {
    console.warn('[API] Network error fetching employees:', error);
    return null;
  }
}

// =============================================================================
// Sync Status (Pull-Sync für Rejection Loop)
// =============================================================================

export interface SheetStatus {
  id: string;
  date: string;
  status: string;
  taLocked: boolean;
  version: number;
  updatedAt: string;
}

export interface SyncStatusResponse {
  foreman_id: string;
  sheets: SheetStatus[];
  server_time: string;
}

/**
 * Holt den Status aller TimeSheets vom Server.
 * Wird verwendet um zu prüfen ob ein Sheet zurückgewiesen wurde.
 */
export async function fetchSyncStatus(foremanId: string): Promise<SyncStatusResponse | null> {
  console.info('[API] Fetching sync status for foreman:', foremanId);

  try {
    const response = await fetch(`/api/sync/status?foreman_id=${encodeURIComponent(foremanId)}`, {
      method: 'GET',
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      console.warn('[API] Failed to fetch sync status: HTTP', response.status);
      return null;
    }

    const data = await response.json();
    console.info('[API] Sync status:', data.sheets?.length ?? 0, 'sheets');
    return data;
  } catch (error) {
    console.warn('[API] Network error fetching sync status:', error);
    return null;
  }
}
